<script setup>
// Inspector do nó "Passar a outro agente": escolhe PARA QUAIS agentes do time
// esta IA pode transferir a conversa. Sem seleção = qualquer agente ativo
// (legado); selecionar restringe a tool e liga o "time" no card dos destinos.
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const currentAgentId = computed(() => Number(route.params.agentId));
const teammates = computed(() =>
  (store.getters['aiAgents/getAgents'] || []).filter(
    a => a.active && a.id !== currentAgentId.value
  )
);

const targetIds = computed(() => (props.data.targetAgentIds || []).map(Number));
const isSelected = id => targetIds.value.includes(Number(id));

const toggleTarget = agent => {
  const next = isSelected(agent.id)
    ? targetIds.value.filter(id => id !== Number(agent.id))
    : [...targetIds.value, Number(agent.id)];
  emit('update', { targetAgentIds: next });
};

onMounted(() => {
  if (!teammates.value.length) store.dispatch('aiAgents/get');
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-ruby-3 text-n-ruby-11"
        >
          <span class="i-lucide-arrow-right-left text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.ROUTE.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.ROUTE.DESC') }}
      </p>
    </div>

    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.ROUTE.TARGETS') }}
    </label>
    <p class="mb-2 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.ROUTE.TARGETS_HINT') }}
    </p>
    <div class="mb-2 flex flex-wrap gap-1.5">
      <button
        v-for="mate in teammates"
        :key="mate.id"
        type="button"
        class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
        :class="
          isSelected(mate.id)
            ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
            : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
        "
        @click="toggleTarget(mate)"
      >
        {{ mate.name }}
      </button>
    </div>
    <p
      v-if="!targetIds.length"
      class="flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
    >
      <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
      <span>{{ t('AI_AGENTS.FLOW.ROUTE.NONE_WARNING') }}</span>
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
